// Client share view — app. Public, view-only page a client opens from a share
// link. Reads the token from location.pathname (/v/<token>), fetches the frozen
// snapshot from the public worker (/api/v/<token>), and renders one of:
// loading / inactive (missing | disabled) / the briefing layout.
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import type { Defect, SourceImage } from "../app/defects/types";
import type { RemItem } from "../app/remediation/data";
import { RemDetail, RemTable } from "./remediation";
import { ReportsList } from "./reports";
import { RequirementsSection } from "./requirements";
import { buildModel, type ShareSnapshot, type SnapshotModel } from "./snapshot";
import { ClientHeader, ShareFooter, SummaryStats, SvMeta, SvSection } from "./ui";
import { ClientViewer } from "./viewer";

type LoadState =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "disabled" }
  | { status: "error" }
  | { status: "ready"; model: SnapshotModel };

function tokenFromPath(): string {
  const seg = location.pathname.split("/").filter(Boolean);
  return seg.length ? seg[seg.length - 1]! : "";
}

async function fetchSnapshot(token: string): Promise<LoadState> {
  const res = await fetch(`/api/v/${encodeURIComponent(token)}`);
  if (res.status === 404) return { status: "missing" };
  if (!res.ok) throw new Error(`status ${res.status}`);
  const data = (await res.json()) as { disabled?: boolean; snapshot?: ShareSnapshot };
  if (data.disabled) return { status: "disabled" };
  if (data.snapshot) return { status: "ready", model: buildModel(data.snapshot) };
  throw new Error("empty payload");
}

async function loadSnapshot(): Promise<LoadState> {
  const token = tokenFromPath();
  if (!token || token === "v") return { status: "missing" };
  // Only a definitive 404 may render as "no longer available" — a client who
  // sees that copy will discard the link. Everything else (network blip, 5xx,
  // malformed payload) retries briefly, then lands on the retryable error
  // screen instead.
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchSnapshot(token);
    } catch {
      if (attempt >= 2) return { status: "error" };
      await new Promise((r) => setTimeout(r, attempt === 0 ? 600 : 1500));
    }
  }
}

// ── inactive link screen ──────────────────────────────────────────────────
function InactiveScreen({ missing }: { missing: boolean }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 22, padding: 24, textAlign: "center" }}>
      <img src="/assets/di-mark-white.svg" alt="Digital Intelligence" style={{ height: 44, width: "auto", display: "block", opacity: 0.9 }} />
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.02em", marginBottom: 9 }}>{missing ? "This link is no longer available" : "This share link is disabled"}</h1>
        <p style={{ fontSize: 13.5, color: "var(--di-subtle)", lineHeight: 1.62, maxWidth: 400 }}>
          {missing ? "The link you followed has been removed or has expired." : "Access through this link has been paused by the team that shared it."} Ask your service provider for a new link.
        </p>
      </div>
      <SvMeta color="var(--di-faint)">Digital Intelligence · dig-int.ai</SvMeta>
    </div>);
}

function ErrorScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 22, padding: 24, textAlign: "center" }}>
      <img src="/assets/di-mark-white.svg" alt="Digital Intelligence" style={{ height: 44, width: "auto", display: "block", opacity: 0.9 }} />
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.02em", marginBottom: 9 }}>Could not load this report</h1>
        <p style={{ fontSize: 13.5, color: "var(--di-subtle)", lineHeight: 1.62, maxWidth: 400 }}>
          Something went wrong while loading the inspection. The link itself is fine. Check your connection and try again.
        </p>
      </div>
      <button onClick={onRetry} style={{ height: 36, padding: "0 18px", borderRadius: 9, border: "1px solid var(--di-orange)", background: "var(--di-orange)", color: "#160d03", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Try again</button>
      <SvMeta color="var(--di-faint)">Digital Intelligence · dig-int.ai</SvMeta>
    </div>);
}

function LoadingScreen() {
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <span className="spin" style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.14)", borderTopColor: "var(--di-orange)" }}></span>
      <SvMeta color="var(--di-subtle)">Loading inspection</SvMeta>
    </div>);
}

// ── briefing layout ───────────────────────────────────────────────────────
function BriefingLayout({ model }: { model: SnapshotModel }) {
  const { snapshot, defects, sourcesById, remItems } = model;
  const [remDetail, setRemDetail] = useState<RemItem | null>(null);
  const a = snapshot.ctx.asset;
  const hasViewer = snapshot.orthos.length > 0 || !!snapshot.model3d || (snapshot.photos ?? []).length > 0;
  const intro = [
    "Findings from the aerial inspection of " + (a?.name || "the asset") + (snapshot.ctx.site ? " at " + snapshot.ctx.site : "") + (snapshot.ctx.inspection ? ", surveyed on " + snapshot.ctx.inspection.date : "") + ".",
    "Everything on this page is view-only. Click a defect pin for its detail and source imagery.",
  ].join(" ");
  const resolveSources = (d: Defect): SourceImage[] => sourcesById.get(d.id) ?? [];
  const reports = snapshot.reports ?? [];
  const requirements = snapshot.requirements ?? [];

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <ClientHeader snapshot={snapshot} />
      <main style={{ flex: 1, width: "100%", maxWidth: 1160, margin: "0 auto", padding: "30px 22px 10px", display: "flex", flexDirection: "column", gap: 34 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <SvMeta color="var(--di-orange)">Inspection findings</SvMeta>
            <h1 style={{ fontSize: 27, fontWeight: 600, color: "var(--di-text)", letterSpacing: "-0.03em", marginTop: 8 }}>
              {a?.name || "Asset"}{a?.ref ? <span style={{ color: "var(--di-subtle)", fontWeight: 500 }}> · {a.ref}</span> : null}
            </h1>
            <p style={{ fontSize: 13.5, color: "var(--di-subtle)", lineHeight: 1.62, marginTop: 8, maxWidth: 680 }}>{intro}</p>
          </div>
          <SummaryStats snapshot={snapshot} defects={defects} remItems={remItems} />
        </div>

        {hasViewer &&
          <SvSection label="Survey" title="Navigatable inspection viewer" sub="Pan and zoom the orthomosaic. Every marker is a recorded defect; click one for its severity, description and drone source imagery.">
            <div style={{ position: "relative", height: 620, borderRadius: 16, overflow: "hidden", border: "1px solid var(--di-border)" }}>
              <ClientViewer model={model} panelDefault onOpenRem={setRemDetail} />
            </div>
          </SvSection>}

        {snapshot.remediation && remItems.length > 0 &&
          <SvSection label="Programme of works" title="Remediation plan" sub="Recommended work items with priorities, trades and cost estimates. Click an item for its scope and the defects it covers.">
            <RemTable items={remItems} defects={defects} onView={setRemDetail} />
          </SvSection>}

        {requirements.length > 0 &&
          <SvSection label="Your questions" title="Client Requirements" sub="What you asked us to establish from this inspection, answered with evidence from the survey: measured quantities, defects, work items and imagery.">
            <RequirementsSection items={requirements} />
          </SvSection>}

        {reports.length > 0 &&
          <SvSection label="Documents" title="Reports" sub="Generated inspection reports for this survey.">
            <ReportsList reports={reports} />
          </SvSection>}
      </main>
      <ShareFooter createdAt={snapshot.createdAt} />
      {remDetail && <RemDetail item={remDetail} defects={defects} resolveSources={resolveSources} onClose={() => setRemDetail(null)} />}
    </div>);
}

// ── app root ──────────────────────────────────────────────────────────────
export function ClientShareApp() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    void loadSnapshot().then((s) => { if (live) setState(s); });
    return () => { live = false; };
  }, [attempt]);

  // Brand accent: the resolved brand's primary colour drives var(--di-orange)
  // for the whole view (§7 #3), so every ported component and reused console
  // control picks it up without per-usage plumbing.
  const accentStyle = useMemo<CSSProperties>(() => {
    if (state.status !== "ready") return {};
    const p = state.model.snapshot.brand.primary;
    return { ["--di-orange" as string]: p, ["--di-orange-hover" as string]: p } as CSSProperties;
  }, [state]);

  return (
    <div style={accentStyle}>
      {state.status === "loading" && <LoadingScreen />}
      {state.status === "missing" && <InactiveScreen missing />}
      {state.status === "disabled" && <InactiveScreen missing={false} />}
      {state.status === "error" && <ErrorScreen onRetry={() => setAttempt((n) => n + 1)} />}
      {state.status === "ready" && <BriefingLayout model={state.model} />}
    </div>);
}
